/**
 * LensSearchBar — Cleic Lens consumer of @cleic/search-bar.
 *
 * Lens is client-only (per F-022 Ledger+Lens Siblings — reads via
 * /api/ledger.json from Ledger). So this bar registers a CLIENT-SIDE source
 * `lens.scenarios` that enumerates PRESETS + localStorage profiles. The
 * federation engine runs entirely in the browser; no /api round trip.
 *
 * Future Phase 4 work can add cross-app federation to Ledger's /api/search
 * (CORS already allows origin-pinning per server/index.ts cors() config).
 *
 * Inherits: E-023 (FAB Method), F-022 (Ledger+Lens Siblings).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  registerSource,
  unregisterSource,
  search,
} from '@cleic/search-bar';
import { SearchBar } from '@cleic/search-bar/ui';
import { PRESETS } from '../data.js';
import { formatPlainNumber } from '../formatting.js';

const PROFILE_STORAGE_KEY = 'cma-dashboard-profiles-v7';

function lensNavigationAction(recordId, primary, route, keywords = []) {
  return {
    sourceId: 'lens.actions',
    recordId,
    kind: 'action',
    primary,
    secondary: 'Navigate within Lens',
    matchedFields: ['primary', 'action', 'navigation', ...keywords],
    action: {
      intent: 'open',
      label: primary,
      route,
      href: route,
      safe: true,
    },
    raw: null,
  };
}

const lensActionItems = [
  lensNavigationAction('actuals.open', 'Open Actuals', '/actuals', ['ledger', 'statements']),
  lensNavigationAction('budgets.open', 'Open Budgets', '/budgets', ['plans', 'payables']),
  lensNavigationAction('forecasts.open', 'Open Forecasts', '/forecasts', ['projection', 'assumptions']),
  lensNavigationAction('support-stack.open', 'Open Support Stack', '/support-stack', ['sub-ledgers', 'schedules', 'trackers']),
  lensNavigationAction('support-stack.write-intents.open', 'Open Write Intents', '/support-stack/write-intents', ['support-stack', 'impact', 'amendments']),
  lensNavigationAction('cash-monitor.open', 'Open Cash Monitor', '/cash-monitor', ['runway', 'daily']),
  lensNavigationAction('synthesis.open', 'Open Synthesis', '/synthesis', ['trajectory', 'variance']),
  lensNavigationAction('drivers.open', 'Open Model Drivers', '/drivers/model-drivers', ['settings', 'scenario']),
  lensNavigationAction('profiles.open', 'Open Model Profiles', '/settings/profiles', ['save', 'scenario']),
];

/**
 * Read user-saved profile scenarios from localStorage. Format mirrors what
 * App.jsx's loadProfilesFromStorage produces — a map of { profileName: scenario }.
 */
function loadUserProfiles() {
  try {
    if (typeof window === 'undefined') return {};
    const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Synthesize a tag list for a scenario based on its flags. Lets the FAB
 * predicate `tag:` filter find scenarios by behavioral signature.
 */
function tagsForScenario(scenario) {
  const tags = [];
  if (scenario.mtbContinuity === false) tags.push('primary-income-off');
  if (scenario.mtbContinuity === true) tags.push('primary-income-on');
  if (scenario.ccPaydownPctOfFreeCash >= 0.5) tags.push('aggressive-paydown');
  if (scenario.ccPaydownPctOfFreeCash <= 0.25 && scenario.ccPaydownPctOfFreeCash !== undefined) {
    tags.push('conservative-paydown');
  }
  if (scenario.fvfTargetMRR && scenario.fvfTargetMRR >= 20000) tags.push('high-mrr');
  if (scenario.fvfTargetMRR && scenario.fvfTargetMRR <= 5000) tags.push('low-mrr');
  if (scenario.cpiAnnualPct && scenario.cpiAnnualPct >= 0.05) tags.push('high-inflation');
  if (scenario.fuelShockPct && scenario.fuelShockPct >= 0.20) tags.push('fuel-shock');
  if (scenario.lifestyleEnabled === false) tags.push('lifestyle-off');
  return tags;
}

/**
 * Synthesize a human-readable description from scenario flags.
 */
function describeScenario(scenario) {
  const bits = [];
  if (scenario.fvfTargetMRR !== undefined) {
    bits.push(`Recurring revenue target ${formatPlainNumber(scenario.fvfTargetMRR, { decimals: 0, scale: false })}`);
  }
  if (scenario.cpiAnnualPct !== undefined) {
    bits.push(`CPI ${(scenario.cpiAnnualPct * 100).toFixed(1)}%`);
  }
  if (scenario.ccPaydownPctOfFreeCash !== undefined) {
    bits.push(`Debt paydown ${(scenario.ccPaydownPctOfFreeCash * 100).toFixed(0)}% of free cash`);
  }
  return bits.join(' · ');
}

const lensScenariosSource = {
  id: 'lens.scenarios',
  app: 'lens',
  visibility: 'PRIVATE',
  weight: 7,
  fields: [
    { name: 'name',        weight: 10, fuzzy: true,  type: 'text'  },
    { name: 'description', weight: 5,  fuzzy: true,  type: 'text'  },
    { name: 'tags',        weight: 6,  fuzzy: false, type: 'exact' },
    { name: 'kind',        weight: 4,  fuzzy: false, type: 'exact' },
  ],
  permissions: () => true,
  query: async ({ plan }) => {
    if (
      plan.fuzzyTerms.length === 0 &&
      plan.exactPhrases.length === 0 &&
      plan.predicates.length === 0
    ) {
      return [];
    }

    const allScenarios = [];

    // Built-in PRESETS (base, upside, downside, …).
    for (const [key, preset] of Object.entries(PRESETS)) {
      allScenarios.push({
        id: `preset:${key}`,
        kind: 'preset',
        name: preset.name || key,
        description: describeScenario(preset),
        tags: tagsForScenario(preset),
        raw: { kind: 'preset', key, scenario: preset },
      });
    }

    // User-saved profiles from localStorage.
    const profiles = loadUserProfiles();
    for (const [profileName, profile] of Object.entries(profiles)) {
      allScenarios.push({
        id: `profile:${profileName}`,
        kind: 'profile',
        name: profileName,
        description: profile?.description || describeScenario(profile),
        tags: tagsForScenario(profile),
        raw: { kind: 'profile', profileName, scenario: profile },
      });
    }

    // Filter by predicates + fuzzy/phrase. The package's ranker handles scoring;
    // we just need to return candidate hits with matchedFields populated.
    const fuzzyTerms = [...plan.fuzzyTerms];
    const phrases = [...plan.exactPhrases];

    const hits = [];
    for (const s of allScenarios) {
      const matchedFields = [];
      let matchesAllPredicates = true;

      // Predicate filtering — only `kind:` and `tag:` interpreted here.
      for (const pred of plan.predicates) {
        if (pred.field === 'kind') {
          if (s.kind !== pred.value.toLowerCase()) {
            matchesAllPredicates = false;
            break;
          }
          matchedFields.push('kind');
        } else if (pred.field === 'tag') {
          if (!s.tags.includes(pred.value)) {
            matchesAllPredicates = false;
            break;
          }
          matchedFields.push('tags');
        }
        // Unknown predicates: ignore (they may target a future source).
      }
      if (!matchesAllPredicates) continue;

      // Fuzzy + phrase matching across name + description + tags.
      const haystack = `${s.name} ${s.description} ${s.tags.join(' ')}`.toLowerCase();
      let anyMatch = fuzzyTerms.length === 0 && phrases.length === 0;

      for (const term of [...fuzzyTerms, ...phrases]) {
        const t = term.toLowerCase();
        if (s.name.toLowerCase().includes(t)) {
          matchedFields.push('name');
          anyMatch = true;
        } else if (s.description.toLowerCase().includes(t)) {
          matchedFields.push('description');
          anyMatch = true;
        } else if (s.tags.some((tag) => tag.toLowerCase().includes(t))) {
          matchedFields.push('tags');
          anyMatch = true;
        } else if (haystack.includes(t)) {
          matchedFields.push('name');
          anyMatch = true;
        }
      }

      // If the query had ONLY predicates (no fuzzy/phrase), it's a match
      // because predicates already passed.
      if (anyMatch || (fuzzyTerms.length === 0 && phrases.length === 0)) {
        hits.push({
          sourceId: 'lens.scenarios',
          recordId: s.id,
          primary: s.name,
          secondary: s.description || `(${s.kind})`,
          matchedFields: [...new Set(matchedFields)],
          raw: s.raw,
          createdAt: new Date(),
        });
      }
    }
    return hits;
  },
};

/**
 * Client-side searchFn: runs the federation engine entirely in-browser.
 * Lens has no /api/search endpoint of its own (F-022 sibling pattern —
 * Lens is a client-only Vite app). All search lives in the browser tab.
 */
async function clientSearchFn(query, ctx, opts) {
  return search(query, ctx, opts);
}

let registered = false;
function ensureRegistered() {
  if (registered) return;
  registerSource(lensScenariosSource);
  registered = true;
}

/**
 * LensSearchBar — same {open, onClose} API as the Ledger version, but the
 * bar runs entirely in the browser with `lens.scenarios` as the only
 * registered source for now. Phase 4 may add cross-app federation to
 * Ledger's /api/search.
 */
export default function LensSearchBar({
  open,
  onClose,
  onSelectScenario,
}) {
  // Register the source ONCE per page lifetime.
  useEffect(() => {
    ensureRegistered();
    return () => {
      // Don't unregister on unmount — the bar may re-open. Cleanup happens
      // on full page reload.
    };
  }, []);

  const ctx = useMemo(
    () => ({ userId: 'lens-user' }),
    []
  );

  return (
    <SearchBar
      open={open}
      onClose={onClose}
      mount="PRIVATE"
      ctx={ctx}
      searchFn={clientSearchFn}
      actionItems={lensActionItems}
      placeholder='Search scenarios… try kind:preset, tag:high-mrr, "downside"'
      onResultOpen={(hit) => {
        if (hit.kind === 'action' && hit.action?.href) {
          window.location.assign(hit.action.href);
          onClose();
          return;
        }
        if (onSelectScenario) {
          onSelectScenario(hit.raw);
        }
        onClose();
      }}
      onResultOpenNewTab={(hit) => {
        if (hit.kind === 'action' && hit.action?.href) {
          window.open(hit.action.href, '_blank', 'noopener,noreferrer');
        }
      }}
    />
  );
}

// Exported for tests + advanced consumers who want to drop the source manually.
export { lensScenariosSource };

// Exported in case Lens has additional one-off cleanups, though typical
// app code never calls this.
export function resetLensSearchRegistration() {
  unregisterSource('lens.scenarios');
  registered = false;
}
